import { useEffect, useState, useRef, createContext, useContext } from "react";
import { NavLink, Link, useNavigate, Outlet, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Wrench } from "lucide-react";
import api from "@/lib/api";
import { LoadingState } from "@/components/ui/States";
import AdminPwaMeta from "@/components/admin/AdminPwaMeta";

const AdminCtx = createContext(null);
export const useAdmin = () => useContext(AdminCtx);

// Primary nav — always visible. The five things John uses daily.
// `end` ensures the Dashboard link doesn't stay "active" on every sub-route.
const PRIMARY_LINKS = [
    { to: "/admin",          label: "Dashboard",  end: true, testId: "admin-nav-dashboard" },
    { to: "/admin/leads",    label: "Leads",                 testId: "admin-nav-leads" },
    { to: "/admin/chats",    label: "Chats",                 testId: "admin-nav-chats" },
    { to: "/admin/photos",   label: "Photos",                testId: "admin-nav-photos" },
    { to: "/admin/reviews",  label: "Reviews",               testId: "admin-nav-reviews" },
    { to: "/admin/storm",    label: "Storm Mode",            testId: "admin-nav-storm" },
];

// Tools — moved off the primary bar to declutter daily use.
// Labels are friendly; URLs are unchanged.
const TOOLS_LINKS = [
    { to: "/admin/verification",       label: "Launch Checklist", testId: "admin-nav-verification" },
    { to: "/admin/automation-events",  label: "Activity Log",     testId: "admin-nav-automation" },
    { to: "/admin/shadow-inbox",       label: "Email Preview",    testId: "admin-nav-shadow-inbox" },
    { to: "/admin/tester-week",        label: "Sandbox",          testId: "admin-nav-tester-week" },
];

const TOOLS_PATHS = new Set(TOOLS_LINKS.map((l) => l.to));

export default function AdminLayout() {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [alertCount, setAlertCount] = useState(0);
    const navigate = useNavigate();

    useEffect(() => {
        if (!user) return undefined;
        const load = () =>
            api.get("/admin/alerts", { params: { unacked_only: true, limit: 1 } })
                .then((r) => setAlertCount(r.data.unacknowledged || 0))
                .catch(() => {});
        load();
        const id = setInterval(load, 60_000);
        return () => clearInterval(id);
    }, [user]);

    useEffect(() => {
        api.get("/admin/auth/me")
            .then((r) => setUser(r.data.user))
            .catch(() => navigate("/admin/login", { replace: true }))
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const logout = async () => {
        try { await api.post("/admin/auth/logout"); } catch { /* ignore */ }
        navigate("/admin/login", { replace: true });
    };

    if (loading) {
        return <div className="a1-container py-10"><LoadingState label="Checking session…" /></div>;
    }
    if (!user) return null;

    return (
        <AdminCtx.Provider value={{ user, logout }}>
            <AdminPwaMeta />
            <div className="min-h-screen" style={{ background: "var(--color-bg)" }}>
                <AdminHeader user={user} onLogout={logout} alertCount={alertCount} />
                <main className="a1-container py-6">
                    <Outlet />
                </main>
            </div>
        </AdminCtx.Provider>
    );
}

function AdminHeader({ user, onLogout, alertCount }) {
    const location = useLocation();
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [toolsOpen, setToolsOpen] = useState(false);
    const toolsRef = useRef(null);

    // Auto-close menus on route change so a click navigates AND collapses.
    useEffect(() => {
        setDrawerOpen(false);
        setToolsOpen(false);
    }, [location.pathname]);

    // Close the Tools dropdown when clicking outside it.
    useEffect(() => {
        if (!toolsOpen) return undefined;
        const onClick = (e) => {
            if (toolsRef.current && !toolsRef.current.contains(e.target)) {
                setToolsOpen(false);
            }
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [toolsOpen]);

    const toolsActive = TOOLS_PATHS.has(location.pathname);

    return (
        <header
            className="sticky top-0 z-40"
            style={{
                background: "#081123",
                borderBottom: "2px solid var(--color-accent)",
                boxShadow: "0 2px 12px rgba(0, 0, 0, 0.35)",
            }}
            data-testid="admin-header"
        >
            <div className="a1-container py-3 flex items-center justify-between gap-3">
                {/* Brand */}
                <Link
                    to="/admin"
                    className="serif text-lg font-bold no-underline shrink-0"
                    style={{ color: "#ffffff" }}
                    data-testid="admin-brand"
                >
                    A1 Admin
                </Link>

                {/* Desktop primary nav (hidden on small screens) */}
                <nav
                    className="hidden md:flex flex-1 min-w-0"
                    aria-label="Admin navigation"
                    data-testid="admin-nav"
                >
                    <ul className="flex items-center gap-1.5 lg:gap-2 m-0 p-0 list-none whitespace-nowrap flex-wrap">
                        {PRIMARY_LINKS.map((link) => (
                            <li key={link.to}>
                                <NavLink
                                    to={link.to}
                                    end={link.end}
                                    data-testid={link.testId}
                                    className={({ isActive }) => `admin-nav-link${isActive ? " is-active" : ""}`}
                                >
                                    {link.label}
                                    {link.to === "/admin/chats" && alertCount > 0 ? (
                                        <span className="admin-nav-alert-badge" data-testid="chats-alert-badge">{alertCount}</span>
                                    ) : null}
                                </NavLink>
                            </li>
                        ))}
                        {/* Tools dropdown — desktop only */}
                        <li className="relative" ref={toolsRef}>
                            <button
                                type="button"
                                onClick={() => setToolsOpen((o) => !o)}
                                aria-expanded={toolsOpen}
                                aria-haspopup="menu"
                                className={`admin-nav-link${toolsActive ? " is-active" : ""}`}
                                data-testid="admin-nav-tools"
                            >
                                <Wrench size={13} className="mr-1.5" /> Tools
                                <ChevronDown
                                    size={14}
                                    className="ml-1 transition-transform"
                                    style={{ transform: toolsOpen ? "rotate(180deg)" : "rotate(0deg)" }}
                                />
                            </button>
                            {toolsOpen ? (
                                <div
                                    className="admin-tools-menu"
                                    role="menu"
                                    data-testid="admin-nav-tools-menu"
                                >
                                    {TOOLS_LINKS.map((link) => (
                                        <NavLink
                                            key={link.to}
                                            to={link.to}
                                            data-testid={link.testId}
                                            role="menuitem"
                                            className={({ isActive }) => `admin-tools-item${isActive ? " is-active" : ""}`}
                                        >
                                            {link.label}
                                        </NavLink>
                                    ))}
                                </div>
                            ) : null}
                        </li>
                    </ul>
                </nav>

                {/* Account block — desktop */}
                <div className="hidden md:flex items-center gap-3 text-sm shrink-0">
                    <span className="hidden lg:inline" style={{ color: "#cbd5e1" }} data-testid="admin-user-email">
                        {user.email} <span style={{ color: "var(--color-accent)" }}>· {user.role}</span>
                    </span>
                    <button
                        onClick={onLogout}
                        className="admin-signout-btn"
                        data-testid="admin-logout"
                        type="button"
                    >
                        Sign out
                    </button>
                </div>

                {/* Hamburger — visible on small screens only */}
                <button
                    type="button"
                    className="md:hidden admin-hamburger-btn"
                    onClick={() => setDrawerOpen(true)}
                    aria-label="Open admin menu"
                    data-testid="admin-nav-hamburger"
                >
                    <Menu size={20} />
                </button>
            </div>

            {/* Mobile drawer */}
            {drawerOpen ? (
                <MobileDrawer
                    user={user}
                    alertCount={alertCount}
                    onClose={() => setDrawerOpen(false)}
                    onLogout={onLogout}
                />
            ) : null}
        </header>
    );
}

function MobileDrawer({ user, alertCount, onClose, onLogout }) {
    return (
        <div
            className="md:hidden fixed inset-0 z-50 flex"
            data-testid="admin-mobile-drawer"
            role="dialog"
            aria-label="Admin menu"
        >
            <div
                className="absolute inset-0"
                style={{ background: "rgba(8, 17, 35, 0.65)" }}
                onClick={onClose}
                aria-hidden="true"
            />
            <div
                className="relative ml-auto w-72 max-w-[85vw] h-full overflow-y-auto"
                style={{ background: "#081123", borderLeft: "2px solid var(--color-accent)" }}
            >
                <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: "1px solid var(--color-line)" }}>
                    <span className="serif text-base font-bold" style={{ color: "#ffffff" }}>A1 Admin</span>
                    <button
                        type="button"
                        onClick={onClose}
                        className="admin-hamburger-btn"
                        aria-label="Close admin menu"
                        data-testid="admin-nav-hamburger-close"
                    >
                        <X size={20} />
                    </button>
                </div>

                <nav className="p-3" aria-label="Admin navigation (mobile)">
                    <p className="admin-drawer-section-label">Main</p>
                    <ul className="m-0 p-0 list-none space-y-1">
                        {PRIMARY_LINKS.map((link) => (
                            <li key={link.to}>
                                <NavLink
                                    to={link.to}
                                    end={link.end}
                                    className={({ isActive }) => `admin-drawer-link${isActive ? " is-active" : ""}`}
                                >
                                    {link.label}
                                    {link.to === "/admin/chats" && alertCount > 0 ? (
                                        <span className="admin-nav-alert-badge">{alertCount}</span>
                                    ) : null}
                                </NavLink>
                            </li>
                        ))}
                    </ul>

                    <p className="admin-drawer-section-label mt-4">
                        <Wrench size={11} className="inline mr-1" /> Tools
                    </p>
                    <ul className="m-0 p-0 list-none space-y-1">
                        {TOOLS_LINKS.map((link) => (
                            <li key={link.to}>
                                <NavLink
                                    to={link.to}
                                    className={({ isActive }) => `admin-drawer-link${isActive ? " is-active" : ""}`}
                                >
                                    {link.label}
                                </NavLink>
                            </li>
                        ))}
                    </ul>
                </nav>

                <div className="p-3 mt-2" style={{ borderTop: "1px solid var(--color-line)" }}>
                    <p className="text-xs mb-2" style={{ color: "#cbd5e1" }}>
                        {user.email}{" "}
                        <span style={{ color: "var(--color-accent)" }}>· {user.role}</span>
                    </p>
                    <button
                        onClick={onLogout}
                        className="admin-signout-btn w-full justify-center"
                        type="button"
                    >
                        Sign out
                    </button>
                </div>
            </div>
        </div>
    );
}

